import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'gatsby'
import { Wrapper, P, Grid, Divider } from '@farewill/ui'
import { GTR, FONT, COLOR } from '@farewill/ui/tokens'
import { screenMin } from '@farewill/ui/helpers/responsive'
import { smallCaps } from '@farewill/ui/helpers/text'

import ArticleAuthorDetails from 'components/ArticleAuthorDetails'
import CallToAction from 'components/CallToAction'
import CardWrapper from 'components/CardWrapper'
import { formatPublishDates } from 'lib/time/formatting'
import { getArticlePath } from 'lib/contentful/articles'
import scrollToElement from 'lib/navigation/scrollToElement'
import { LIVE_PRODUCTS } from 'lib/products/constants'

import { SECTION_TYPES } from '../constants'
import NextArticleLink from './NextArticleLink'
import Widget from './Widget'
import SectionStepsWrapper from '../components/SectionStepsWrapper'
import Header from './Header'
import Callout from '../components/Callout'
import Footer from '../components/Footer'
import Splash from '../components/Splash'
import Content from '../components/Content'
import RichContent from '../components/RichContent'
import RichImage from '../components/RichImage'

const StyledDescription = styled(P)`
  font-size: ${FONT.SIZE.L};
  margin-bottom: ${GTR.L};
`

const StyledLinksList = styled.ol`
  a {
    text-decoration: none;
    position: relative;
    &:hover {
      color: ${COLOR.BLACK};
      text-decoration: underline;
    }
  }
  li {
    a {
      padding-bottom: 6px;
      display: inline-block;
    }
  }
  li + li {
    a {
      padding-top: 6px;
    }
  }
`

const StyledCardWrapper = styled(CardWrapper)`
  max-width: none;
  .card-header {
    ${smallCaps};
  }
`

const RelatedArticles = ({ className, relatedArticles }) => (
  <div className={className}>
    <StyledCardWrapper
      header="RELATED ARTICLES"
      hasBoldHeadingText
      hasWideHeader
    >
      <StyledLinksList>
        {relatedArticles.map(({ slug, type, title }) => (
          <Wrapper tag="li" margin={[0, 0, 'XS']} marginFromM={[0]} key={slug}>
            <Link to={getArticlePath(slug, type)}>{title}</Link>
          </Wrapper>
        ))}
      </StyledLinksList>
    </StyledCardWrapper>
  </div>
)

const StyledDesktopRelatedArticlesWrapper = styled(Wrapper)`
  display: none;
  ${screenMin.xl`
    display: block;
  `}
`

const StyledMobileRelatedArticlesWrapper = styled(Wrapper)`
  ${screenMin.xl`
    display: none;
  `}
`

const ContentsLinks = styled.a`
  display: inline-block;
`

const getCustomComponents = (data) =>
  data?.map((section) => {
    let newData = section

    if (section.steps) {
      newData = { ...newData, steps: section.steps }
    }

    return newData || section
  })

const HelpArticle = ({ data: { article, nextArticleInGuide } }) => {
  const { cta } = article

  const contentsAnchors = (article.sections || [])
    .filter(
      ({ __typename }) =>
        [SECTION_TYPES.CONTENT, SECTION_TYPES.STEPS].indexOf(__typename) > -1
    )
    .map(({ slug: id, title: text }) => ({ id, text }))

  const articleSections = getCustomComponents(article.sections) || []

  let articleWidget
  if (
    articleSections.length &&
    // eslint-disable-next-line no-underscore-dangle
    articleSections[0].__typename === SECTION_TYPES.WIDGET
  ) {
    articleWidget = articleSections.shift()
  }

  const breadcrumbs =
    article?.articleCategories?.length > 0
      ? article.articleCategories[0]
      : undefined

  return (
    <Wrapper background={COLOR.WHITE}>
      <Header article={article} breadcrumbs={breadcrumbs} />
      <Grid container gap={['XL', 'M']} gapFromXL={GTR.XL}>
        <Grid.Item
          startRow={1}
          spanFromM={10}
          spanFromL={8}
          spanFromXL={7}
          startColumnFromM={2}
          startColumnFromL={3}
          startColumnFromXL={4}
        >
          {article.author && (
            <ArticleAuthorDetails
              slug={article.author.slug}
              image={article.author.image}
              name={article.author.name}
              title={article.author.title}
            />
          )}

          {article.description && (
            <StyledDescription>
              {article.description.description}
            </StyledDescription>
          )}

          {articleWidget && (
            <Wrapper margin={['L', 0]} marginFromM={['L', 0, 'XXL']}>
              <Widget type={articleWidget.type} />
            </Wrapper>
          )}
        </Grid.Item>
        <Grid.Item
          startRow={2}
          spanRowsFromXL={3}
          startRowFromXL={1}
          spanFromM={10}
          spanFromL={8}
          spanFromXL={3}
          startColumnFromM={2}
          startColumnFromL={3}
          startColumnFromXL={1}
        >
          <StyledCardWrapper
            highlight
            header="CONTENTS"
            hasBoldHeadingText
            hasWideHeader
          >
            <StyledLinksList>
              {contentsAnchors.map(({ id, text }) => (
                <Wrapper tag="li" margin={[0, 0, 'XS']} key={id}>
                  <ContentsLinks
                    onClick={(e) =>
                      scrollToElement({
                        event: e,
                        elementId: id,
                        offset: -parseInt(GTR.M, 10),
                      })
                    }
                    alt={`Jump to ${text}`}
                    href={`#${id}`}
                  >
                    {text}
                  </ContentsLinks>
                </Wrapper>
              ))}
            </StyledLinksList>
          </StyledCardWrapper>

          {article.relatedArticles && (
            <StyledDesktopRelatedArticlesWrapper margin={['L', 0, 0]}>
              <RelatedArticles relatedArticles={article.relatedArticles} />
            </StyledDesktopRelatedArticlesWrapper>
          )}
        </Grid.Item>

        <Grid.Item
          startRow={3}
          startRowFromXL={2}
          spanFromM={10}
          spanFromL={8}
          spanFromXL={7}
          startColumnFromM={2}
          startColumnFromL={3}
          startColumnFromXL={4}
        >
          <Divider margin={['L', 0]} marginFromM={['XL', 0, 'M']} />
          {article?.richContent?.raw && (
            <RichContent data={article.richContent} />
          )}

          {(articleSections || []).map(
            ({ __typename: sectionType, ...section }) => (
              <React.Fragment key={section.slug || section.type || section.id}>
                {[
                  SECTION_TYPES.CONTENT,
                  SECTION_TYPES.STEPS,
                  SECTION_TYPES.RICH_IMAGE,
                ].indexOf(sectionType) > -1 && (
                  <Content
                    title={section.title}
                    slug={section.slug}
                    sectionType={sectionType}
                  >
                    {sectionType === SECTION_TYPES.CONTENT &&
                      section.richContent && (
                        <RichContent data={section.richContent} />
                      )}
                    {sectionType === SECTION_TYPES.STEPS && (
                      <SectionStepsWrapper data={section} />
                    )}
                    {sectionType === SECTION_TYPES.RICH_IMAGE && (
                      <RichImage data={section} />
                    )}
                  </Content>
                )}
                {sectionType === SECTION_TYPES.SPLASH && (
                  <Splash
                    title={section.title}
                    content={section.content}
                    cta={section.cta}
                    image={section.image}
                    teamMember={section.teamMember}
                    product={article.product}
                  />
                )}
                {sectionType === SECTION_TYPES.CALLOUT && (
                  <Callout
                    title={section.title}
                    content={section.richContent}
                    cta={section.cta}
                    secondCta={section.secondCta}
                  />
                )}
                {sectionType === SECTION_TYPES.WIDGET && (
                  <Wrapper margin={['L', 0]} marginFromM={['XL', 0]}>
                    <Widget type={section.type} />
                  </Wrapper>
                )}
              </React.Fragment>
            )
          )}

          <P size="S" margin={['L', 0, 'XL']} marginFromL={['L', 0, 'XXL']}>
            Article reviewed {}
            <time dateTime={article.reviewedAt || article.publishedAt}>
              {formatPublishDates(article.reviewedAt || article.publishedAt)}
            </time>
          </P>

          {nextArticleInGuide && (
            <NextArticleLink
              title={nextArticleInGuide.title}
              link={getArticlePath(
                nextArticleInGuide.slug,
                nextArticleInGuide.type
              )}
              image={nextArticleInGuide.image}
            />
          )}

          {cta && (
            <CallToAction
              title={cta.title}
              htmlDescription={cta?.description?.description}
              buttonText={cta.buttonText}
              buttonLink={cta.buttonLink}
              isTelephoneNumber={cta.isTelephoneNumber}
            />
          )}

          {article.relatedArticles && (
            <StyledMobileRelatedArticlesWrapper>
              <RelatedArticles relatedArticles={article.relatedArticles} />
            </StyledMobileRelatedArticlesWrapper>
          )}
        </Grid.Item>
      </Grid>
      {article.footer && (
        <Wrapper>
          <Footer
            title={article.footer.title}
            description={article.footer.description}
            cta={article.footer.cta}
            trustmarks={article.footer.trustmarks}
            product={article.product}
          />
        </Wrapper>
      )}
    </Wrapper>
  )
}

HelpArticle.propTypes = {
  data: PropTypes.shape({
    article: PropTypes.shape({
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      description: PropTypes.shape({
        description: PropTypes.string.isRequired,
      }),
      metaTitle: PropTypes.string,
      metaDescription: PropTypes.shape({
        metaDescription: PropTypes.string.isRequired,
      }),
      articleCategories: PropTypes.arrayOf(
        PropTypes.shape({
          shortTitle: PropTypes.string,
          slug: PropTypes.string,
        })
      ),
      backgroundType: PropTypes.string,
      intro: PropTypes.shape({
        intro: PropTypes.string.isRequired,
      }),
      widget: PropTypes.shape({
        type: PropTypes.string,
      }),
      image: PropTypes.shape({
        file: PropTypes.shape({
          url: PropTypes.string.isRequired,
        }).isRequired,
        title: PropTypes.string.isRequired,
      }),
      teamMember: PropTypes.shape({
        name: PropTypes.string,
        title: PropTypes.string,
        image: PropTypes.shape({
          file: PropTypes.shape({
            url: PropTypes.string.isRequired,
          }).isRequired,
          title: PropTypes.string.isRequired,
        }),
      }),
      product: PropTypes.oneOf(LIVE_PRODUCTS).isRequired,
      publishedAt: PropTypes.string.isRequired,
      reviewedAt: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired,
      richContent: PropTypes.shape({
        raw: PropTypes.string.isRequired,
      }),
      sections: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.shape({
            __typename: PropTypes.oneOf([SECTION_TYPES.CONTENT]),
            title: PropTypes.string.isRequired,
            slug: PropTypes.string.isRequired,
            richContent: PropTypes.shape({
              raw: PropTypes.string.isRequired,
            }).isRequired,
          }),
          PropTypes.shape({
            __typename: PropTypes.oneOf([SECTION_TYPES.WIDGET]),
            type: PropTypes.string.isRequired,
          }),
        ])
      ).isRequired,
      author: PropTypes.shape({
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        image: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
      }).isRequired,
      relatedArticles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          title: PropTypes.string.isRequired,
          slug: PropTypes.string.isRequired,
          type: PropTypes.string.isRequired,
        })
      ),
      cta: PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.shape({
          description: PropTypes.string,
        }).isRequired,
        buttonText: PropTypes.string.isRequired,
        buttonLink: PropTypes.string.isRequired,
        isTelephoneNumber: PropTypes.bool.isRequired,
      }),
      footer: PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        cta: PropTypes.shape({
          title: PropTypes.string.isRequired,
          description: PropTypes.shape({
            description: PropTypes.string,
          }),
          link: PropTypes.string,
        }),
        trustmarks: PropTypes.string.isRequired,
      }),
    }).isRequired,
    nextArticleInGuide: PropTypes.shape({
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      image: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    }),
  }).isRequired,
}

RelatedArticles.propTypes = {
  className: PropTypes.string,
  relatedArticles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
}

RelatedArticles.defaultProps = {
  className: '',
}

export default HelpArticle
